import { Inject, Injectable } from '@angular/core';
import { NetworkGroupApiService } from '@fleet/api/network-group/network-group-api.service';
import {
  DriverModel,
  DriverStatus,
  FunctionPermissionModel,
  FuseNavigationItem,
  GroupHierarchyItem,
  LifeCycleStage,
  NetworkGroupModel,
  NetworkUserModel,
  OperatorModel,
  OperatorUserModel,
  OrganisationModel,
  OrganisationUserModel,
  TravellerModel,
} from '@fleet/model';
import { decodeToken } from '@fleet/utilities';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { fleetFlexNavigation } from './navigation-menus/flex-navigation';
import { networkNavigation } from './navigation-menus/network-navigation';

import { LocaleService } from '@fleet/locale/locale-service/locale.service';
import { organisationMenu } from './navigation-menus/organisation-menu';
import { logoutNavigationMenu } from './navigation-menus/shared-navigation';
import {
  filterNavigationItemsByFunctions,
  filterNavigationItemsForFleet,
} from './navigation-security';

import { TranslocoService } from '@jsverse/transloco';
import { operatorUserNavigation } from './navigation-menus/operator-user-navigation';

@Injectable({
  providedIn: 'root',
})
export class FleetNavigationService {
  currentUser: NetworkUserModel;
  navigation: BehaviorSubject<any> = new BehaviorSubject([]);
  currentGroup: BehaviorSubject<GroupHierarchyItem> = new BehaviorSubject(null);
  currentOrganisation: BehaviorSubject<OrganisationModel> = new BehaviorSubject(
    null
  );
  currentOperator: BehaviorSubject<OperatorModel> = new BehaviorSubject(null);
  flexNavigation: boolean;
  functions: FunctionPermissionModel[];
  nativeAppNavigation: BehaviorSubject<any> = new BehaviorSubject([]);
  envName: string;

  constructor(
    private networkGroupApiService: NetworkGroupApiService,

    private localeService: LocaleService,
    private translocoService: TranslocoService,
    @Inject('env') env: any
  ) {
    this.envName = env.envName;
  }

  get navigation$(): Observable<FuseNavigationItem[]> {
    return this.navigation.asObservable();
  }

  get currentGroup$(): Observable<GroupHierarchyItem> {
    return this.currentGroup.asObservable();
  }

  get nativeAppNavigation$() {
    return this.nativeAppNavigation.asObservable();
  }

  get currentOrganisation$() {
    return this.currentOrganisation.asObservable();
  }

  get currentOperator$() {
    return this.currentOperator.asObservable();
  }

  setNavigation(navigation: FuseNavigationItem[], functions: any[]) {
    let secured = filterNavigationItemsByFunctions(navigation, functions);

    secured = filterNavigationItemsForFleet(
      secured,
      this.currentUser,
      this.envName
    );

    this.navigation.next(secured);
  }

  get currentOrganisationAndGroup() {
    return combineLatest();
  }

  clearNavigation() {
    this.navigation.next([]);
  }

  setFunctions(functions: FunctionPermissionModel[]) {
    this.functions = functions;
  }

  /** Avoid organisation/{id}//segment when menu links start with `/`. */
  private stripLeadingSlashForOrgPath(
    segment: string | undefined | null
  ): string {
    if (segment == null) {
      return '';
    }
    return segment.startsWith('/') ? segment.slice(1) : segment;
  }

  //this is for HUB only
  setCurrentOrganisation(
    organisation: OrganisationModel,
    functions?: FunctionPermissionModel[]
  ) {
    //check if this is the same on the id
    this.currentOrganisation.next(organisation);
    if (organisation) {
      const orgNavigationIndex = networkNavigation()[0].children.findIndex(
        (n: any) => n.id === 'organisation'
      );
      let newMenu = filterNavigationItemsByFunctions(
        organisationMenu(),
        functions
      );
      newMenu = filterNavigationItemsForFleet(
        newMenu,
        this.currentUser,
        this.envName
      );
      const orgNavChildren = newMenu.map(
        (orgNavigationItem: FuseNavigationItem) => {
          return {
            ...orgNavigationItem,
            children: orgNavigationItem.children
              ? orgNavigationItem.children
                  .filter((c: any) => c.id !== 'organisation-kiosk')
                  .map((childItem: any) => {
                    if (childItem.type === 'basic') {
                      return {
                        ...childItem,
                        link: organisation
                          ? 'organisation/' +
                            organisation.organisationId +
                            '/' +
                            this.stripLeadingSlashForOrgPath(childItem.link)
                          : childItem.link,
                        classes: {
                          wrapper: 'bg-gray-800 -mx-2 pl-4 -mb-2 ',
                        },
                      };
                    } else {
                      //this is a collapsable menu item
                      return {
                        ...childItem,
                        link: childItem.link
                          ? organisation
                            ? 'organisation/' +
                              organisation.organisationId +
                              '/' +
                              this.stripLeadingSlashForOrgPath(childItem.link)
                            : childItem.link
                          : null,
                        classes: {
                          wrapper: 'bg-gray-800 -mx-2 pl-4 -mb-2 ',
                        },
                        children: childItem.children.map(
                          (collapsedChildItem: any) => {
                            return {
                              ...collapsedChildItem,
                              link: organisation
                                ? 'organisation/' +
                                  organisation.organisationId +
                                  '/' +
                                  this.stripLeadingSlashForOrgPath(
                                    collapsedChildItem.link
                                  )
                                : collapsedChildItem.link,
                            };
                          }
                        ),
                      };
                    }
                  })
              : [],
            link:
              'organisation/' +
              organisation.organisationId +
              '/' +
              this.stripLeadingSlashForOrgPath(orgNavigationItem.link),
            classes: {
              wrapper: 'bg-gray-800 -mx-2 pl-4 -mb-2',
            },
          };
        }
      );
      const orgNav = {
        type: 'group',
        id: 'current.organisation',
        title: organisation.name,
        ignoreTranslation: true,
        classes: {
          wrapper: 'bg-gray-800 -mx-2 px-4 pt-3 h-16',
          title: 'text-white',
          host: 'bg-gray-800',
        },
        children: [
          ...orgNavChildren,
          {
            type: 'group',
            classes: { wrapper: 'bg-gray-800  -mx-2' },
          },
        ],
      } as any;
      const networkNavigationClone = JSON.parse(
        JSON.stringify(this.navigation.value)
      );
      const newNavigation = networkNavigationClone.map((n: any) => {
        //Does this ALREADY have a current org? If it does - replace it, otherwise, we are adding it
        const exists = n.children.find(
          (child: any) => child.id === 'current.organisation'
        );
        n.children.splice(orgNavigationIndex + 1, exists ? 1 : 0, orgNav);
        return n;
      });
      this.navigation.next(newNavigation);
    } else {
      //remove dividers and current org if present
      const currentNavigationClone = JSON.parse(
        JSON.stringify(this.navigation.value)
      );
      const cleanedNavigation = currentNavigationClone.map((n: any) => {
        const cleanedChildren = n.children
          ? n.children.filter((s: any) => {
              if (s.type === 'divider' || s.id === 'current.organisation') {
                return false;
              }
              return true;
            })
          : [];
        return { ...n, children: cleanedChildren };
      });
      this.navigation.next(cleanedNavigation);
    }
  }

  setCurrentOperator(operator: OperatorModel) {
    console.log('setCurrentOperator called with:', {
      operatorId: operator?.operatorId,
      operatorName: operator?.displayName,
    });

    if (operator?.operatorId !== this.currentOperator.value?.operatorId) {
      this.currentOperator.next(operator);

      if (operator) {
        const operatorNavigationIndex =
          networkNavigation()[0].children.findIndex(
            (n: any) => n.id === 'operator'
          );
        console.log(
          'Found operator navigation index:',
          operatorNavigationIndex
        );

        const operatorNavChildren = [
          {
            id: 'overview',
            title: 'overview',
            icon: 'widgets',
            link: '/home',
            type: 'basic',
            securityFunctions: ['OPERATOR_DETAIL:read'],
            exactMatch: true,
          },
          {
            id: 'vehicle',
            title: 'vehicles',
            icon: 'local_taxi',
            type: 'basic',
            link: '/vehicle',
            securityFunctions: ['VEHICLE_SEARCH:read'],
            exactMatch: true,
          },
          {
            id: 'medallion',
            title: 'medallions',
            type: 'basic',
            icon: 'shield_with_house',
            link: '/vehicle/medallion',
            securityFunctions: ['MEDALLION_SEARCH:read'],
          },
          {
            id: 'insurance',
            title: 'insurance',
            type: 'basic',
            icon: 'history_edu',
            link: '/vehicle/insurance',
          },
        ];
        console.log('Created operator nav children:', operatorNavChildren);

        const mappedNavChildren = operatorNavChildren.map(
          (operatorNavItem: any) => {
            const mapped = {
              ...operatorNavItem,
              link: 'operator/' + operator.operatorId + operatorNavItem.link,
              classes: {
                wrapper: 'bg-gray-800 -mx-2 pl-4 -mb-2',
              },
            };
            console.log('Mapped nav item:', {
              original: operatorNavItem.id,
              newLink: mapped.link,
            });
            return mapped;
          }
        );

        const operatorNavGroup = {
          type: 'group',
          id: 'current.operator',
          title: operator.displayName,
          ignoreTranslation: true,
          classes: {
            wrapper: 'bg-gray-800 -mx-2 px-4 pt-3 h-16',
            title: 'text-white',
            host: 'bg-gray-800',
          },
          children: [
            ...mappedNavChildren,
            {
              type: 'group',
              classes: { wrapper: 'bg-gray-800  -mx-2' },
            },
          ],
        };
        console.log('Created operator nav group:', {
          id: operatorNavGroup.id,
          title: operatorNavGroup.title,
          childrenCount: operatorNavGroup.children.length,
        });

        const networkNavigationClone = JSON.parse(
          JSON.stringify(this.navigation.value)
        );
        console.log('Current navigation structure:', {
          groupCount: networkNavigationClone.length,
          firstGroupChildrenCount: networkNavigationClone[0]?.children?.length,
        });

        let newNavigation = [...networkNavigationClone];

        // Before splice
        console.log('Navigation before splice:', {
          operatorNavigationIndex,
          currentChildren: newNavigation[0]?.children?.map(
            (c: FuseNavigationItem) => ({
              id: c.id,
              title: c.title,
            })
          ),
        });

        if (newNavigation.length > 0 && newNavigation[0].children) {
          newNavigation[0].children.splice(
            operatorNavigationIndex + 1,
            0,
            operatorNavGroup
          );

          // After splice
          console.log('Navigation after splice:', {
            operatorNavigationIndex,
            insertedOperator: operatorNavGroup.title,
            updatedChildren: newNavigation[0].children,
          });
        } else {
          console.warn(
            'Unable to add operator navigation: invalid navigation structure',
            {
              navigationLength: newNavigation.length,
              hasChildren: newNavigation[0]?.children != null,
            }
          );
        }

        newNavigation = filterNavigationItemsByFunctions(
          newNavigation,
          this.functions
        );
        console.log('Navigation after function filtering:', {
          groupCount: newNavigation.length,
          firstGroupChildrenCount: newNavigation[0]?.children?.length,
        });

        newNavigation = filterNavigationItemsForFleet(
          newNavigation,
          this.currentUser,
          this.envName
        );
        console.log('Navigation after fleet filtering:', {
          groupCount: newNavigation.length,
          firstGroupChildrenCount: newNavigation[0]?.children?.length,
        });

        console.log('Setting final operator navigation');
        this.navigation.next(newNavigation);
      } else {
        console.log('Removing operator from navigation');
        const currentNavigationClone = JSON.parse(
          JSON.stringify(this.navigation.value)
        );

        const cleanedNavigation = currentNavigationClone.map((n: any) => {
          const cleanedChildren = n.children
            ? n.children.filter((s: any) => {
                const keep = !(
                  s.type === 'divider' || s.id === 'current.operator'
                );
                if (!keep) {
                  console.log('Removing navigation item:', {
                    id: s.id,
                    type: s.type,
                  });
                }
                return keep;
              })
            : [];
          return { ...n, children: cleanedChildren };
        });
        console.log('Setting null operator navigation:', {
          groupCount: cleanedNavigation.length,
          firstGroupChildrenCount: cleanedNavigation[0]?.children?.length,
        });
        this.navigation.next(cleanedNavigation);
      }
    }
  }

  setCurrentGroupByNetworkGroup(group: NetworkGroupModel | any) {
    const currentGroup = {
      displayName: group?.displayName,
      groupId: group?.networkGroupId,

      type: group?.type,
      link: '/group/' + group?.networkGroupId,
    } as GroupHierarchyItem;

    if (!currentGroup.groupId) {
      currentGroup.groupId = group?.groupId;
    }
    if (!currentGroup.displayName) {
      currentGroup.displayName = group?.groupName;
    }

    // Store current operator before setting group
    const currentOperator = this.currentOperator.value;

    this.setCurrentGroup(currentGroup);

    // Restore operator navigation if it existed
    if (currentOperator) {
      this.setCurrentOperator(currentOperator);
    }
  }

  setCurrentGroup(group: any) {
    //find if group is child of
    let newGroup = null;

    if (
      this.currentGroup.value &&
      group &&
      group.parentId === this.currentGroup.value.groupId
    ) {
      newGroup = { ...group, parent: this.currentGroup.value };
    } else {
      newGroup = group;
    }

    this.currentGroup.next(newGroup);
    if (!this.flexNavigation) {
      let navigation = networkNavigation().map((navigationItem: any) => {
        const children = navigationItem.children;

        return {
          ...navigationItem,

          title: group ? group.displayName : null,
          ignoreTranslation: true,
          classes: { title: 'text-white' },
          children: children.map((childItem: any) => {
            if (childItem.id === 'signout') {
              return childItem;
            }
            if (childItem.type === 'basic') {
              return {
                ...childItem,
                link: childItem.link,
              };
            } else {
              //this is a collapsable menu item
              const updatedCollaspable = {
                ...childItem,
                link: childItem.link ? childItem.link : null,
                children: childItem.children.map((collapsedChildItem: any) => {
                  return {
                    ...collapsedChildItem,
                    link: collapsedChildItem.link,
                  };
                }),
              };
              return updatedCollaspable;
            }
          }),
        };
      });
      navigation = filterNavigationItemsByFunctions(navigation, this.functions);

      navigation = filterNavigationItemsForFleet(
        navigation,
        this.currentUser,
        this.envName
      );

      this.navigation.next(navigation);
    } else {
      let navigation = filterNavigationItemsByFunctions(
        fleetFlexNavigation,
        this.functions
      );

      navigation = filterNavigationItemsForFleet(
        navigation,
        this.currentUser,
        this.envName
      );
      this.navigation.next(navigation);
    }
  }

  clearGroupFromNavigation() {
    this.navigation.next({ ...this.navigation.value });
  }

  configureMenuForUser(
    type: string,
    functions: FunctionPermissionModel[],
    user: any
  ) {
    this.currentUser = user;
    this.functions = functions;
    if (type === 'NETWORK') {
      //networkUser
      this.configureMenuForNetworkUser(user, functions, null);
    } else if (type === 'ORGANISATION') {
      //organisation user
      this.configureMenuForOrganisationUser(user, functions);
    } else if (type === 'STAFF') {
      //staff
    } else if (type === 'DRIVER') {
      this.configureMenuForDriver(user, functions);
      //driver
    } else if (type === 'TRAVELLER') {
      //fleet
      this.configureMenuForTraveller(user);
    } else if (type === 'OPERATOR') {
      this.configureMenuForOperatorUser(user, functions);
    }
  }
  configureMenuForNetworkUser(
    networkUser: NetworkUserModel,
    functions: FunctionPermissionModel[],
    networkGroup: NetworkGroupModel
  ) {
    // Check if the URL has a flex layout parameter
    const flexParam = window.location.search.includes('layout=flex');
    if (flexParam) {
      // If flex layout is detected, we might need special handling
      console.log('Flex layout detected for network user');
    }
    const jwt = localStorage.getItem('access_token');
    const decodedToken = decodeToken(jwt);
    decodedToken.groups = [];
    if (networkGroup) {
      this.setCurrentGroupByNetworkGroup(networkGroup);
    } else {
      let navigation = filterNavigationItemsByFunctions(
        flexParam ? (fleetFlexNavigation as any[]) : networkNavigation(),
        functions
      );
      navigation = filterNavigationItemsForFleet(
        navigation,
        networkUser,
        this.envName
      );
      this.navigation.next(navigation);
    }
  }

  configureMenuForOrganisationUser(
    organisationUser: OrganisationUserModel,
    functions: any
  ) {
    let newMenu = filterNavigationItemsByFunctions(
      organisationMenu(),
      functions
    );
    newMenu = filterNavigationItemsForFleet(
      newMenu,
      organisationUser,
      this.envName
    );
    this.navigation.next([
      {
        type: 'group',
        children: [...newMenu, logoutNavigationMenu],
      },
    ]);
  }

  configureMenuForOperatorUser(
    operatorUser: OperatorUserModel,
    functions: any
  ) {
    const newMenu = filterNavigationItemsByFunctions(
      [
        {
          id: 'vehicle',
          title: 'vehicles',
          icon: 'local_taxi',
          type: 'basic',
          link: '/vehicle',
          securityFunctions: ['VEHICLE_SEARCH:read'],
        },
        ...operatorUserNavigation,
      ],

      functions
    );

    this.navigation.next([
      {
        type: 'group',
        children: [...newMenu, logoutNavigationMenu],
      },
    ]);
  }

  configureMenuForDriver(driver: DriverModel, functions: any) {
    if (
      (driver.lifecycleStage === LifeCycleStage.Opportunity &&
        driver.status === DriverStatus.Incomplete) ||
      (driver.lifecycleStage === LifeCycleStage.Customer &&
        driver.status == DriverStatus.Incomplete)
    ) {
      //registration so just logout and the driver container will handle the menu for customers
      this.navigation.next([
        {
          id: 'logout',
          title: 'logout',
          type: 'basic',
          icon: 'heroicons_outline:logout',
          link: '/auth/logout',
        },
      ]);
    } else {
    }
  }

  configureMenuForTraveller(traveller: TravellerModel) {
    this.navigation.next([
      {
        type: 'group',
        children: [
          {
            id: 'job.new',
            title: 'newTrip',
            type: 'basic',
            icon: 'drive_eta',
            link: '/traveller/job/new',
          },
          {
            id: 'job.upcoming',
            title: 'upcomingTrips',
            type: 'basic',
            icon: 'event',
            link: '/traveller/job/upcoming',
          },
          {
            id: 'job.past',
            title: 'pastTrips',
            type: 'basic',
            icon: 'history',
            link: '/traveller/job/history',
          },
          {
            id: 'traveller.settings',
            title: 'settings',
            type: 'basic',
            icon: 'heroicons_outline:cog',
            link: '/traveller/settings/personal',
          },
          {
            id: 'logout',
            title: 'logout',
            type: 'basic',
            icon: 'heroicons_outline:logout',
            link: '/auth/logout',
          },
        ],
      },
    ]);
  }
}
